import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar, Clock, User } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import TutoringCalendar from "@/components/tutoring/TutoringCalendar";
import { ListToolbar } from "@/components/common/ListToolbar";
import { DataPagination } from "@/components/common/DataPagination";
import { LoadingState } from "@/components/common/LoadingState";
import { ErrorState } from "@/components/common/ErrorState";
import { EmptyState } from "@/components/common/EmptyState";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import { queryKeys } from "@/lib/queryKeys";
import { useUrlState } from "@/lib/urlState";
import { exportRowsAsCsv } from "@/lib/csv";
import { toast } from "sonner";
import { format } from "date-fns";

interface TutoringSchedule {
  id: string;
  instructor_id: string;
  student_email: string;
  student_name: string | null;
  session_date: string;
  start_time: string;
  end_time: string;
  subject: string;
  topic: string | null;
  notes: string | null;
  status: string;
  created_at: string;
}

interface InstructorProfile {
  first_name: string;
  last_name: string;
}

const formatTime = (time: string) => {
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
};

const getStatusColor = (
  status: string,
): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case "scheduled":
      return "default";
    case "completed":
      return "secondary";
    case "cancelled":
      return "destructive";
    case "rescheduled":
      return "outline";
    default:
      return "default";
  }
};

export default function StudentTutoringSchedules() {
  const { user } = useAuth();
  const url = useUrlState();

  const tab = url.get("tab") ?? "list";
  const search = url.get("q") ?? "";
  const statusFilter = url.get("status") ?? "upcoming";
  const todayIso = new Date().toISOString().slice(0, 10);

  const [instructorProfiles, setInstructorProfiles] = useState<
    Record<string, InstructorProfile>
  >({});

  const paginated = usePaginatedQuery<TutoringSchedule>({
    queryKey: queryKeys.tutoringSchedules.list({
      page: 0,
      pageSize: 25,
      filters: {
        role: "student",
        search,
        statusFilter,
        email: user?.email,
      },
    }),
    table: "tutoring_schedules",
    honorSoftDelete: true,
    orderBy: { column: "session_date", ascending: statusFilter !== "past" },
    syncToUrl: tab === "list",
    enabled: !!user?.email && tab === "list",
    filters: (q) => {
      let next = q.eq("student_email", user!.email!);
      if (statusFilter === "upcoming") {
        next = next.gte("session_date", todayIso).eq("status", "scheduled");
      } else if (statusFilter === "past") {
        next = next.lt("session_date", todayIso);
      }
      if (search) {
        next = next.or(`subject.ilike.%${search}%,topic.ilike.%${search}%`);
      }
      return next;
    },
  });

  useEffect(() => {
    paginated.setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  // Hydrate instructor names for the rows on the current page.
  useEffect(() => {
    const ids = Array.from(
      new Set(paginated.rows.map((s) => s.instructor_id).filter(Boolean)),
    ).filter((id) => !instructorProfiles[id]);
    if (ids.length === 0) return;
    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name")
        .in("id", ids);
      if (error) {
        console.error("Error loading instructor profiles:", error);
        return;
      }
      setInstructorProfiles((prev) => {
        const next = { ...prev };
        for (const p of data ?? []) {
          next[p.id] = {
            first_name: p.first_name || "",
            last_name: p.last_name || "",
          };
        }
        return next;
      });
    })();
  }, [paginated.rows, instructorProfiles]);

  const getInstructorName = (instructorId: string) => {
    const profile = instructorProfiles[instructorId];
    if (profile && (profile.first_name || profile.last_name)) {
      return `${profile.first_name} ${profile.last_name}`.trim();
    }
    return "Instructor";
  };

  const handleExport = () => {
    if (paginated.rows.length === 0) {
      toast.error("Nothing to export on this page");
      return;
    }
    exportRowsAsCsv(
      `my-tutoring-${statusFilter}.csv`,
      paginated.rows as unknown as Record<string, unknown>[],
      [
        "session_date",
        "start_time",
        "end_time",
        "subject",
        "topic",
        "status",
        "instructor_id",
      ],
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      <Header />

      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            My Tutoring Sessions
          </h1>
          <p className="text-muted-foreground">
            View your scheduled tutoring sessions with instructors.
          </p>
        </div>

        <Tabs
          value={tab}
          onValueChange={(v) => url.set({ tab: v, page: null })}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="list">List View</TabsTrigger>
            <TabsTrigger value="calendar">Calendar View</TabsTrigger>
          </TabsList>

          <TabsContent value="calendar" className="mt-6">
            <TutoringCalendar instructorId={user?.email || ""} isInstructor={false} />
          </TabsContent>

          <TabsContent value="list" className="mt-6 space-y-4">
            <ListToolbar
              search={search}
              onSearchChange={(v) => url.set({ q: v || null, page: null })}
              searchPlaceholder="Search subject or topic…"
              onExportCsv={handleExport}
              filters={
                <Select
                  value={statusFilter}
                  onValueChange={(v) => url.set({ status: v, page: null })}
                >
                  <SelectTrigger className="w-40 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="upcoming">Upcoming</SelectItem>
                    <SelectItem value="past">Past</SelectItem>
                    <SelectItem value="all">All sessions</SelectItem>
                  </SelectContent>
                </Select>
              }
            />

            {paginated.isError ? (
              <ErrorState onRetry={() => paginated.refetch()} />
            ) : paginated.isLoading ? (
              <LoadingState />
            ) : paginated.rows.length === 0 ? (
              <EmptyState
                title="No sessions"
                description="Your instructors will schedule sessions and they'll appear here."
              />
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    {paginated.rows.map((schedule) => (
                      <div
                        key={schedule.id}
                        className="p-4 border rounded-lg bg-card"
                      >
                        <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                          <div>
                            <h3 className="font-medium text-lg mb-1">
                              {schedule.subject}
                            </h3>
                            {schedule.topic && (
                              <p className="text-sm text-muted-foreground">
                                Topic: {schedule.topic}
                              </p>
                            )}
                          </div>
                          <Badge variant={getStatusColor(schedule.status)}>
                            {schedule.status.charAt(0).toUpperCase() +
                              schedule.status.slice(1)}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                          <div className="flex items-center gap-2 text-sm">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span>{getInstructorName(schedule.instructor_id)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span>
                              {format(
                                new Date(schedule.session_date),
                                "EEE, MMM dd, yyyy",
                              )}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span>
                              {formatTime(schedule.start_time)} -{" "}
                              {formatTime(schedule.end_time)}
                            </span>
                          </div>
                        </div>

                        {schedule.notes && (
                          <div className="mt-3 p-3 bg-muted/50 rounded-md">
                            <p className="text-sm font-medium mb-1">
                              Notes from instructor:
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {schedule.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <DataPagination
              page={paginated.page}
              pageSize={paginated.pageSize}
              total={paginated.total}
              pageCount={paginated.pageCount}
              onPageChange={paginated.setPage}
              onPageSizeChange={paginated.setPageSize}
              isFetching={paginated.isFetching}
            />
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
}
